import { toOriginal } from "../PlayerCore/timeline.js"
import { projectCtas } from "../PlayerCore/timelineView.js"

/**
 * SC-1323 §4.4: the podcast page's props → PlayerShell tracks. Overlays (chapters, CTAs,
 * captions, transcript, switch) only exist when the audio was taken from this video
 * (timeline.video_matches): then both share the original timeline apart from the DC insertions.
 * Pro publishes chapters and CTAs on the assembled-audio timeline; the video track gets them
 * projected onto the original timeline, the audio track as published.
 */
export function buildPodcastTracks({ podcast, episode, video, masterUrl, transcriptUrl }) {
    if (!video?.available || !masterUrl) {
        return { tracks: [], sameTimeline: false }
    }

    const matches = episode.timeline?.video_matches === true
    const insertions = episode.timeline?.insertions ?? []
    const artworkUrl = episode.artwork || podcast.artwork || null
    // Sorted by published time (stable): the stable tie-break for equal starts after projection.
    const published = [...(episode.chapters ?? [])].sort((a, b) => (a.from_time ?? 0) - (b.from_time ?? 0))
    const chapter = (source, start) => ({ id: String(source.id), title: source.title ?? "", start, image: source.image ?? null })
    const transcripts = matches ? (episode.transcripts ?? []).map((transcript) => ({ lang: transcript.lang, label: transcript.label, url: transcriptUrl(transcript.lang) })) : []

    const videoTrack = {
        id: `video:${episode.hash_id}`,
        itemId: episode.hash_id,
        kind: "video",
        title: episode.title,
        artworkUrl,
        media: { hls: { masterUrl, duration: video.duration ?? 0 } },
        chapters: matches ? published.map((source) => chapter(source, toOriginal(source.from_time ?? 0, insertions))) : [],
        ctas: matches ? projectCtas(episode.ctas, { mode: "video", insertions }) : [],
        transcripts,
        timeline: { insertions: [] },
    }

    if (!matches) {
        return { tracks: [videoTrack], sameTimeline: false }
    }

    const audioTrack = {
        id: `audio:${episode.hash_id}`,
        itemId: episode.hash_id,
        kind: "audio",
        title: episode.title,
        artworkUrl,
        media: { audio: { url: episode.audio?.url ?? "", duration: episode.audio?.duration ?? 0 } },
        chapters: published.map((source) => chapter(source, source.from_time ?? 0)),
        ctas: projectCtas(episode.ctas, { mode: "audio", insertions }),
        transcripts,
        timeline: { insertions: insertions.map((insertion) => ({ atMs: insertion.at_ms, durationMs: insertion.duration_ms })) },
    }

    return { tracks: [videoTrack, audioTrack], sameTimeline: true }
}
